/**
 * Firestore collection & document path builders. Kept in the shared package so
 * the FE data-access adapter (ADR-0003) and the BE repositories use identical
 * paths — a renamed collection is a one-line change, not a cross-repo hunt.
 */
export const paths = {
    /** Account-level state, one doc per signed-in user (doc 07 §4.2). */
    accounts: () => 'accounts',
    account: (uid) => `accounts/${uid}`,
    businesses: () => 'businesses',
    business: (bizId) => `businesses/${bizId}`,
    services: (bizId) => `businesses/${bizId}/services`,
    service: (bizId, serviceId) => `businesses/${bizId}/services/${serviceId}`,
    /** Bookable staff members with per-person schedules (doc 09 §5.1, §5.5). */
    staff: (bizId) => `businesses/${bizId}/staff`,
    staffMember: (bizId, staffId) => `businesses/${bizId}/staff/${staffId}`,
    /** Availability lives in a single well-known doc. */
    availabilityDoc: (bizId) => `businesses/${bizId}/availability/weekly`,
    /** Regulatory bundles, one per `${country}_${numberType}` (doc 05 §3). */
    bundles: (bizId) => `businesses/${bizId}/bundles`,
    bundle: (bizId, key) => `businesses/${bizId}/bundles/${key}`,
    /** Purchased/in-flight numbers; doc id is a requestId idempotency key. */
    numbers: (bizId) => `businesses/${bizId}/numbers`,
    number: (bizId, numberId) => `businesses/${bizId}/numbers/${numberId}`,
    appointments: (bizId) => `businesses/${bizId}/appointments`,
    appointment: (bizId, apptId) => `businesses/${bizId}/appointments/${apptId}`,
    /** Conversation lifecycle records (doc 33 W7). Server-only; the reconciler
     * finalises abandoned ones. Document id = session id. */
    sessions: (bizId) => `businesses/${bizId}/sessions`,
    session: (bizId, sessionId) => `businesses/${bizId}/sessions/${sessionId}`,
    /** Durable mutation results (doc 33 W6). Server-only; written in the same
     * transaction as the appointment change. Document id = operation id. */
    operations: (bizId) => `businesses/${bizId}/operations`,
    operation: (bizId, operationId) => `businesses/${bizId}/operations/${operationId}`,
    /** Published, validated business configuration (doc 33 W9). `published` is
     * the last known-good snapshot calls run on; `readiness` the owner-facing
     * check. Server-write-only. */
    configDocs: (bizId) => `businesses/${bizId}/config`,
    publishedConfig: (bizId) => `businesses/${bizId}/config/published`,
    configReadiness: (bizId) => `businesses/${bizId}/config/readiness`,
    calls: (bizId) => `businesses/${bizId}/calls`,
    call: (bizId, callSid) => `businesses/${bizId}/calls/${callSid}`,
    /** Per-business client base (doc 11); document id is the E.164 phone number. */
    clients: (bizId) => `businesses/${bizId}/clients`,
    client: (bizId, e164) => `businesses/${bizId}/clients/${e164}`,
    /** Outbound SMS ledger (doc 21 D7). Document id is deterministic —
     * `{appointmentId}_{kind}` — so creating it IS the idempotency claim.
     * Server-write-only; owners read it for delivery status and the GDPR record. */
    notifications: (bizId) => `businesses/${bizId}/notifications`,
    notification: (bizId, notificationId) => `businesses/${bizId}/notifications/${notificationId}`,
    /** Owner-email ledger (doc 29 D5). Document id is deterministic —
     * `{appointmentId}_{kind}` — so creating it IS the idempotency claim. Its own
     * collection rather than a row in `notifications`, because that ledger's `to`
     * is an E.164 number and its ids are claims for CLIENT messages.
     * Server-write-only; owners read it to see what was sent and what was not. */
    emailNotifications: (bizId) => `businesses/${bizId}/emailNotifications`,
    emailNotification: (bizId, emailNotificationId) => `businesses/${bizId}/emailNotifications/${emailNotificationId}`,
    /** One row per call already counted against the minute allowance (doc 26 D2).
     * Document id is the CallSid, so creating it IS the idempotency claim — a
     * redelivered trigger cannot bill the same conversation twice. Server-only,
     * and never read back except by that claim. */
    usageEvents: (bizId) => `businesses/${bizId}/usageEvents`,
    usageEvent: (bizId, callSid) => `businesses/${bizId}/usageEvents/${callSid}`,
    /** Calls with appointment changes still waiting for the call to end
     * (doc 22 D5). Document id is the CallSid. Written when a change is deferred,
     * deleted once reconciled — so a row older than the longest possible call is
     * a call that never finalised, and the sweep can find it without scanning
     * appointments. Server-only. */
    pendingCalls: (bizId) => `businesses/${bizId}/pendingCalls`,
    pendingCall: (bizId, callSid) => `businesses/${bizId}/pendingCalls/${callSid}`,
    /** Per-business daily usage counters; doc id is the local-date key `YYYY-MM-DD`
     * (business timezone). Server-only — backs the daily call cap. */
    usage: (bizId) => `businesses/${bizId}/usage`,
    usageDay: (bizId, day) => `businesses/${bizId}/usage/${day}`,
    // ── Prepaid billing (doc 42 §7). Everything below except `billingHistory` is
    // server-only in both directions; the owner reads the safe projections
    // (`billingSummary`, `billingPreferences` on the business doc, and history).
    /** Canonical wallet aggregate: balance, reservations, warning cycle, order
     * lock, consent and Stripe mappings. One doc, id `current`. */
    billingState: (bizId) => `businesses/${bizId}/billingState/current`,
    /** Operator anti-loop counter for billing SMS, one doc per UTC day. Kept
     * out of the outbox so it never looks like (or triggers) a job. */
    billingSmsQuota: (bizId, day) => `businesses/${bizId}/billingState/smsQuota_${day}`,
    /** Last verdict on the latest subscription invoice (doc 61 R04). */
    billingAllowanceCheck: (bizId) => `businesses/${bizId}/billingState/allowanceCheck`,
    /** Immutable signed seconds deltas. `balanceSeconds = Σ deltaSeconds`. */
    billingLedger: (bizId) => `businesses/${bizId}/billingLedger`,
    billingLedgerEntry: (bizId, entryId) => `businesses/${bizId}/billingLedger/${entryId}`,
    /** One minute-package purchase: immutable quote, Stripe ids, lifecycle. */
    topupOrders: (bizId) => `businesses/${bizId}/topupOrders`,
    topupOrder: (bizId, orderId) => `businesses/${bizId}/topupOrders/${orderId}`,
    /** Short-lived server-priced quotes; a purchase names one by id. */
    topupQuote: (bizId, quoteId) => `businesses/${bizId}/topupQuotes/${quoteId}`,
    /** Owner-readable payment projection of an order (no secrets, no payloads). */
    billingHistory: (bizId) => `businesses/${bizId}/billingHistory`,
    billingHistoryItem: (bizId, orderId) => `businesses/${bizId}/billingHistory/${orderId}`,
    /** Per-call metering: cumulative charged seconds, reservation, lease. Id = callKey. */
    billingUsage: (bizId) => `businesses/${bizId}/billingUsage`,
    billingUsageEntry: (bizId, callKey) => `businesses/${bizId}/billingUsage/${callKey}`,
    /** Auto top-up spend per calendar month (`YYYY-MM`, billing timezone). */
    billingPeriod: (bizId, periodKey) => `businesses/${bizId}/billingPeriods/${periodKey}`,
    /** Durable payment / SMS work with lease + retry. */
    billingOutbox: (bizId) => `businesses/${bizId}/billingOutbox`,
    billingOutboxJob: (bizId, jobId) => `businesses/${bizId}/billingOutbox/${jobId}`,
    /** One subscription-period allowance (doc 59): grant/expiry/reversal audit. */
    billingAllowances: (bizId) => `businesses/${bizId}/billingAllowances`,
    billingAllowance: (bizId, allowanceId) => `businesses/${bizId}/billingAllowances/${allowanceId}`,
    /** Server-created card-setup attempts (doc 59 F01), keyed by Checkout session id. */
    billingSetupAttempts: (bizId) => `businesses/${bizId}/billingSetupAttempts`,
    billingSetupAttempt: (bizId, sessionId) => `businesses/${bizId}/billingSetupAttempts/${sessionId}`,
    /** Verified Stripe webhook inbox, keyed by event id. Top level, server-only. */
    stripeEvents: () => 'stripeEvents',
    stripeEvent: (eventId) => `stripeEvents/${eventId}`,
    /** Top-level reverse index; document id is the E.164 number. */
    phoneNumbers: () => 'phoneNumbers',
    phoneNumber: (e164) => `phoneNumbers/${e164}`,
    /** Inventory of shared inbound numbers we own (doc 18 §5.1). Document id is
     * the E.164 number. Separate from `phoneNumbers` on purpose: that is a
     * hot-path routing index, this is lifecycle bookkeeping. */
    numberPool: () => 'numberPool',
    numberPoolEntry: (e164) => `numberPool/${e164}`,
    /** Top-level reverse index for shared inbound numbers (doc 18): the caller's
     * ORIGINAL destination — the business's own diverted line — mapped to the
     * business. Document id is the country-scoped NSN key (`PL-600200300`), built
     * by `divertSourceKey()`, NOT an E.164 number. */
    /**
     * Demo calls from the setup wizard (doc 13a §6a) — kept for analytics, and
     * kept OUT of the business's own data on purpose: a throwaway conversation
     * must not appear in the owner's call history, feed their KPIs, or look like
     * something a customer did. Top-level, keyed by nothing the portal reads.
     */
    demoCalls: () => 'demoCalls',
    /** One admitted conversation and the budget unit it spent (doc 33 W8).
     * Top level, server-only; id = `<provider>_<providerCallId>`. Also the
     * persisted call→business route for status callbacks. */
    admissions: () => 'admissions',
    admission: (id) => `admissions/${id}`,
    demoCall: (id) => `demoCalls/${id}`,
    divertSources: () => 'divertSources',
    divertSource: (key) => `divertSources/${key}`,
    /** Calls that reached one of our numbers but could not be bound to a business
     * (doc 18 §6.5). Should be empty; each row is a lost customer call. */
    unattributedCalls: () => 'unattributedCalls',
    unattributedCall: (id) => `unattributedCalls/${id}`,
    /** Account-email ledger — verify address, reset password (doc 88 D6). Top
     * level because these emails are about a PERSON, not a business; id =
     * `{uid}_{kind}_{minute}`, so creating it is the claim. Server-only. */
    authEmails: () => 'authEmails',
    authEmail: (id) => `authEmails/${id}`,
    /** Per-address send limits (doc 88 D7); id = sha256 of the lowercased
     * address, so no address ever appears in a document path. Server-only. */
    authEmailThrottle: (addressHash) => `authEmailThrottle/${addressHash}`,
    /** One doc per UTC day: every account email we sent that day (doc 88 D7).
     * Server-only. */
    authEmailBudget: (utcDay) => `authEmailBudget/${utcDay}`,
};
export const AVAILABILITY_DOC_ID = 'weekly';
//# sourceMappingURL=collections.js.map